<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";
import Icon from "./Icon.vue";
import IconButton from "./IconButton.vue";
import RailProfile from "./RailProfile.vue";
import RailJourney from "./onboarding/RailJourney.vue";
import type { MenuItem } from "./Dropdown.vue";
import { session } from "../composables/session";
import { can } from "../composables/permissions";
import { t } from "../i18n";

/**
 * The workspace's navigation rail, the same on every screen: Atendimentos, Campanhas (for who
 * sees them), Fluxos, Contatos, Agenda (for who sees it), Relatórios and Configurações, the setup
 * journey and the profile.
 */
const props = withDefaults(
  defineProps<{
    slug: string;
    active?:
      | "inbox"
      | "campaigns"
      | "flows"
      | "contacts"
      | "agenda"
      | "reports"
      | "settings"
      | null;
    /** Unread conversations on the Atendimentos icon. */
    badge?: number;
    /** The journey item opens its landing (the inbox passes it). */
    landing?: boolean;
    /** Extra items of the profile menu (presence, on the inbox). */
    extra?: MenuItem[];
  }>(),
  { active: null, badge: 0, landing: false, extra: undefined },
);
const items = computed(() =>
  (
    [
      ["inbox", "chat", "nav.inbox", "inbox"],
      ["campaigns", "megaphone", "nav.campaigns", "campaigns"],
      ["flows", "grip", "nav.flows", "flows"],
      ["contacts", "user", "nav.contacts", "contacts"],
      ["agenda", "calendar", "nav.agenda", "agenda"],
      ["reports", "poll", "nav.reports", "reports"],
      ["settings", "settings", "nav.settings", "settings"],
    ] as const
  ).filter(
    ([id]) =>
      (id !== "campaigns" || can(props.slug, "campaigns.read")) &&
      (id !== "agenda" || can(props.slug, "appointments.read")),
  ),
);
</script>
<template>
  <nav class="nav-rail" :aria-label="t('nav.main')">
    <div class="nav-primary">
      <div v-for="[id, icon, label, path] in items" :key="id" class="nav-item">
        <!-- Conversas stays put when active, as on WhatsApp Web (the open chat stays open); the
             other sections go back to their list from one of their items (a campaign, a flow). -->
        <IconButton
          v-if="active === id && id === 'inbox'"
          :icon="icon"
          :label="t(label)"
          active
        />
        <RouterLink
          v-else
          :to="`/${path}`"
          class="icon-button"
          :class="{ active: active === id }"
          :aria-current="active === id ? 'page' : undefined"
          :title="t(label)"
          :aria-label="t(label)"
          ><Icon :name="icon" :size="22"
        /></RouterLink>
        <span v-if="id === 'inbox' && badge" class="badge nav-badge">{{
          badge
        }}</span>
      </div>
      <RailJourney :slug="slug" :landing="landing" />
    </div>
    <div class="nav-bottom">
      <RouterLink
        v-if="session.me?.platformRole"
        to="/styleguide"
        class="icon-button"
        :aria-label="t('common.styleguide')"
        :title="t('common.styleguide')"
        ><Icon name="book" :size="20"
      /></RouterLink>
      <RailProfile :slug="slug" :extra="extra" />
    </div>
  </nav>
</template>
